import type { Question } from "./control";
import type { Activity, LivePayload, TextAnswer } from "./types";

/**
 * ═══ CE QUE LA MODÉRATION REGARDE ═══
 *
 * Sorti en fonctions pures parce que ce sont les seules règles de tout l'écran, et parce
 * que ce qui décide quelque chose ne doit pas vivre dans une fermeture — la leçon de
 * `mergeLive`, qui reportait les réponses d'une activité sur la suivante hors
 * d'atteinte de la suite de tests.
 */

/** Les trois états d'une question, plus « toutes ». */
export type Bucket = "received" | "kept" | "discarded" | "all";

export const BUCKETS: ReadonlyArray<{ key: Bucket; label: string }> = [
  { key: "received", label: "En attente" },
  { key: "kept", label: "Validées" },
  { key: "discarded", label: "Écartées" },
  { key: "all", label: "Toutes" },
];

/**
 * COMBIEN DANS CHAQUE PILE.
 *
 * ⚠️ L'ÉTAT PEUT MANQUER. Le serveur ne l'envoie qu'à la régie (`serializeTextAnswers`) :
 * une charge de participant n'en porte pas, et une question sans état est une question
 * REÇUE, pas une question d'une quatrième sorte.
 */
export function countByBucket(
  answers: ReadonlyArray<TextAnswer>,
): Record<Bucket, number> {
  const counts: Record<Bucket, number> = {
    received: 0,
    kept: 0,
    discarded: 0,
    all: answers.length,
  };
  for (const answer of answers) counts[answer.state ?? "received"] += 1;
  return counts;
}

/**
 * LA PILE DEMANDÉE, ET CE QU'ON Y CHERCHE.
 *
 * ⚠️ UNE RECHERCHE PARCE QU'IL Y EN A BEAUCOUP. C'est la raison d'être de cet écran :
 * une plénière de huit cents personnes empile des centaines de questions, et
 * « retrouver celle du monsieur au fond » ne se fait pas en défilant.
 *
 * ⚠️ INSENSIBLE À LA CASSE ET AUX ACCENTS. On tape vite, entre deux prises de parole,
 * et « energie » doit trouver « énergie » — sinon la recherche ne sert qu'à ceux qui
 * savent déjà comment la question était écrite.
 */
export function filterAnswers<T extends TextAnswer>(
  answers: ReadonlyArray<T>,
  bucket: Bucket,
  search = "",
): T[] {
  const needle = fold(search);
  return answers.filter((answer) => {
    if (bucket !== "all" && (answer.state ?? "received") !== bucket) return false;
    return !needle || fold(answer.text).includes(needle);
  });
}

/**
 * ═══ TOUTES LES QUESTIONS, ET CELLES DE L'ÉCRAN À LA SECONDE (#341) ═══
 *
 * La lecture de toutes les questions (`useQuestions`) ne se relit qu'aux gestes et
 * toutes les trente secondes ; la charge du direct, elle, arrive à chaque question
 * posée, mais ne porte que celles de l'activité à l'écran. On prend donc chacune là
 * où elle est la plus fraîche : l'activité à l'écran depuis le direct, les autres
 * depuis la lecture.
 *
 * ⚠️ REMPLACÉES, PAS AJOUTÉES. Les deux sources portent les questions de l'activité à
 * l'écran : les ajouter doublerait chaque ligne, et une remise à zéro laisserait
 * dans la liste les questions qu'elle vient d'effacer.
 *
 * ⚠️ PUIS RANGÉES PAR DATE, comme la route les rend. Posées en bout de liste, les
 * questions de l'écran passaient après celles d'une session plus récente.
 */
export function withLiveQuestions(
  questions: ReadonlyArray<Question>,
  live: Pick<LivePayload, "activity" | "answers"> | undefined,
): Question[] {
  const activity = live?.activity;
  if (activity?.type !== "questions" || !live?.answers) return [...questions];

  return [
    ...questions.filter((q) => q.activityId !== activity.id),
    ...live.answers.map((answer) => ({ ...answer, activityId: activity.id })),
  ].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id - b.id);
}

/** Les activités où le public pose ses questions, dans l'ordre de la régie. */
export function questionActivities<T extends Pick<Activity, "type">>(
  activities: ReadonlyArray<T>,
): T[] {
  return activities.filter((a) => a.type === "questions");
}

/** Les questions d'une activité, ou toutes quand aucune n'est choisie. */
export function ofActivity<T extends Pick<Question, "activityId">>(
  questions: ReadonlyArray<T>,
  activityId: number | null,
): T[] {
  return activityId === null
    ? [...questions]
    : questions.filter((q) => q.activityId === activityId);
}

/**
 * L'ACTIVITÉ FILTRÉE, RAMENÉE À CE QUE LE SÉLECTEUR PEUT ENCORE EXPRIMER.
 *
 * ⚠️ LA MÊME RÈGLE QUE `offeredType` DANS LA RÉGIE, pour la même raison : le filtre
 * ne se lève que par son `<select>`, qui ne se dessine qu'à partir de deux activités.
 * Supprimer l'activité regardée laissait sinon l'écran filtré sur une activité
 * disparue — donc vide, et sans plus rien pour revenir à « Toutes ».
 */
export function offeredActivity(
  activityId: number | null,
  activities: ReadonlyArray<Pick<Activity, "id">>,
): number | null {
  return activities.length > 1 && activities.some((a) => a.id === activityId)
    ? activityId
    : null;
}

/**
 * ═══ LA MODÉRATION D'UNE ACTIVITÉ, DEPUIS SA LIGNE DE LA RÉGIE (#451) ═══
 *
 * ⚠️ LE SEUL CHEMIN ÉTAIT EN BAS DE LA RÉGIE, sous toutes les activités : une session
 * d'une dizaine de lignes faisait défiler jusqu'au bout pour modérer celle qu'on vient
 * de lancer. La ligne porte maintenant le sien, et il arrive filtré sur elle.
 *
 * ⚠️ L'ADRESSE DIT LAQUELLE, parce que la modération s'ouvre dans un autre onglet
 * (#373) : elle ne partage aucun état avec la régie, seulement ce qu'on lui écrit.
 */
export const ACTIVITY_PARAM = "activite";

/** Le chemin de la modération, filtrée sur une activité quand on en donne une. */
export function moderationPath(activityId?: number): string {
  return activityId === undefined
    ? "/live/questions"
    : `/live/questions?${ACTIVITY_PARAM}=${activityId}`;
}

/**
 * L'activité que demande l'adresse, ou aucune.
 *
 * ⚠️ UNE ADRESSE SE TAPE ET SE COPIE À LA MAIN : ce qui n'est pas un identifiant ne
 * filtre rien, plutôt que de filtrer sur `NaN` et de montrer une liste vide.
 */
export function activityFromParam(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** Sans casse ni accents : `NFD` sépare la lettre de son signe, on retire le signe. */
function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}
